import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [key, setKey] = useState("");
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { "X-Admin-Key": key },
      });
      setEntries(res.data.entries);
    } catch (err) {
      setEntries(null);
      setError(
        err.response?.status === 401
          ? "Invalid admin key."
          : "Could not load the waitlist."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "rosteno-waitlist.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed.");
    }
  };

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[#0A0E17] text-white px-6 md:px-12 py-16"
    >
      <a
        data-testid="admin-back-link"
        href="/"
        className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#8B99AE] transition-colors duration-300 hover:text-white"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to site
      </a>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-2xl mx-auto mt-20"
      >
        <span className="flex items-center justify-center w-12 h-12 border border-white/25 mb-8">
          <Lock className="w-5 h-5" />
        </span>
        <h1
          data-testid="admin-heading"
          className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight"
        >
          Waitlist <span className="italic text-[#8B99AE]">admin.</span>
        </h1>

        <form onSubmit={load} className="mt-10 flex flex-col sm:flex-row gap-4">
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            autoComplete="off"
            className="flex-1 bg-transparent border-0 border-b border-white/25 focus:border-white rounded-none px-0 py-4 text-base placeholder:text-[#8B99AE]/50 focus:outline-none transition-colors duration-300"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={!key || loading}
            className="font-mono-accent text-xs uppercase tracking-[0.2em] rounded-full bg-gradient-to-r from-[#34D399] to-[#38BDF8] text-black px-8 py-4 transition-opacity duration-300 hover:opacity-85 disabled:opacity-50"
          >
            {loading ? "Loading..." : "View list"}
          </button>
        </form>

        {error && (
          <p
            data-testid="admin-error"
            className="mt-6 font-mono-accent text-xs uppercase tracking-[0.2em] text-red-400"
          >
            {error}
          </p>
        )}

        {entries && (
          <motion.div
            data-testid="admin-waitlist-table"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12"
          >
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <span
                data-testid="admin-count"
                className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#8B99AE]"
              >
                {entries.length} {entries.length === 1 ? "signup" : "signups"}
              </span>
              <button
                data-testid="admin-download-csv-button"
                onClick={downloadCsv}
                className="flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/25 px-4 py-2.5 transition-colors duration-300 hover:bg-white hover:text-black"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
            <ul className="divide-y divide-white/10">
              {entries.map((entry, i) => (
                <li
                  key={entry.email}
                  data-testid={`admin-entry-${i + 1}`}
                  className="flex items-center justify-between py-4 text-sm"
                >
                  <span>{entry.email}</span>
                  <span className="font-mono-accent text-xs text-[#8B99AE]">
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleDateString()
                      : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
